/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import {
    AdHocReasonCode,
    DoseRoundStatus,
    FacilityGroupConfigurationDto,
    GroupPermissionsRes,
    HSAdministeredDose,
    HSAdministeredDrug,
    HSDoseRound,
    HSDrug,
    HSPackedMedication,
    HSPackedPatientDay,
    HSPackedPatientPrnMedication,
    HSPackedPrnMedication,
    HSPatchObservation,
    HSPatient,
    MedicationStatus,
    ReasonCode,
    WardDto,
    SyringeDriverRunActivityDto,
    SyringeDriverRunDto,
    SyringeDriverRunScheduledActivityDto,
    PatchApplicationDto,
    PatchApplicationActivityDto,
    PatchApplicationScheduledActivityDto,
    PatchApplicationScheduledActivityStatusDto,
    PatchApplicationScheduledActivityTypeDto,
    PatchApplicationActivityTypeDto
} from 'server-openapi';
import {CreateRoundSegmentOp, SyncRounds, UpdateRoundSegmentOp} from '../SyncRounds';
import * as datefns from 'date-fns';
import {addHours, isSameDay} from 'date-fns';
import itiriri from 'itiriri';
import {DateUtils, Interval} from '../../core/utils/dateUtils';
import {DrugUtils, MedicationInformation} from './DrugUtils';
import {PatientUtils} from './PatientUtils';
import {isMedicationActive} from './PackedPatientDayUtils';
import {User} from '../../core/authn/user-management';
import {ResidentDetailsUtils} from './ResidentDetailsUtils';
import {
    DrugAdministrationUtils,
} from '../../pages/ResidentDetails/components/MedicationListsTabbedRouter/DrugAdministrationUtils';
import {groupBy, range} from 'lodash';
import {
    FilterDrugType,
    FilterDrugTypeControlled,
    FilterDrugTypeOther,
} from '../../pages/Dashboard/DashboardPage/DashboardPage';
import {FacilityGroupUtils} from './FacilityGroupUtils';
import {SyncTestResults} from '../SyncTestResults';
import {
    PatchStatus,
} from '../../pages/ResidentDetails/components/MedicationListsTabbedRouter/TabLists/PatchesMedicationList';
import {PatchUtils} from '../../pages/ResidentDetails/components/patches/PatchUtils';
import {
    PatchApplicationStatusDto,
    SyringeDriverRunScheduledActivityStatusDto
} from "server-openapi/dist/generated-sdk/api";
import {getRoundHierarchyForAdministeredDrug} from "../../view_model/RoundHierarchy";

export interface RoundUtilStores {
  packedDayStore: ReadonlyMap<string, HSPackedPatientDay>;
  patientStore: ReadonlyMap<string, HSPatient>;
  drugStore: ReadonlyMap<string, HSDrug>;
  packedPrnStore: ReadonlyMap<string, HSPackedPatientPrnMedication>;
  roundStore: ReadonlyMap<string, HSDoseRound>;
  facilityStore: ReadonlyMap<string, WardDto>;
  facilityGroupConfigurationStore: ReadonlyMap<string, FacilityGroupConfigurationDto>;
  syringeDriverRunStore: ReadonlyMap<string, SyringeDriverRunDto>;
  patchApplicationStore: ReadonlyMap<string, PatchApplicationDto>;
}

export class RoundUtils {
  private stores: RoundUtilStores;

  constructor(apiStores: RoundUtilStores) {
    this.stores = apiStores;
  }

  public canAdministerEverything = (groupPermissions: GroupPermissionsRes) => canAdministerEverything(groupPermissions);
  public canAdminister = (groupPermissions: GroupPermissionsRes, secondCheckable: boolean, patchSighting: boolean, medicationInformation: MedicationInformation) => canAdminister(groupPermissions, secondCheckable, patchSighting, medicationInformation);
  public isScheduleItemVisible = (facilityGroupId: number,
      groupPermissions: GroupPermissionsRes,
      drugStore: ReadonlyMap<string, HSDrug>,
      residentDetailsUtils: ResidentDetailsUtils,
      scheduleItem: RoundScheduleItem,
      selectedDrugTypeControlled?: FilterDrugTypeControlled,
      selectedDrugTypeOther?: FilterDrugTypeOther
  ) => isScheduleItemVisible(facilityGroupId, groupPermissions, drugStore, residentDetailsUtils, scheduleItem, selectedDrugTypeControlled, selectedDrugTypeOther);

  public isPatchSighting = (scheduleItem: RoundScheduleItem) => isPatchSighting(scheduleItem);
  public isControlledDrugId = (drugId: number) => isControlledDrugId(drugId, this.stores.drugStore);
  public getFilteredMedicationsByDrugType = (packedMedication: HSPackedMedication[], drugType?: FilterDrugType) =>
    getFilteredMedicationsByDrugType(this.stores.drugStore, packedMedication, drugType);
  public getScheduleFromParameters = (
    interval: Interval,
    facilityIds: number[],
      includeAdministered: boolean // Typically this should be false as we usually only want to get RoundScheduleItems that haven't yet been administered
  ) => getScheduleFromParameters(interval, facilityIds, this.stores, includeAdministered);
  
  public getSyringeDriverRunScheduleFromParameters = (
      interval: Interval,
      facilityIds: number[]
  ) => getSyringeDriverRunScheduleFromParameters(interval, facilityIds, this.stores);

    public getPatchApplicationScheduleFromParameters = (
        interval: Interval,
        facilityIds: number[]
    ) => getPatchApplicationScheduleFromParameters(interval, facilityIds, this.stores);

  public getPackedMedicationForAdministeredDrug = (administeredDrug: HSAdministeredDrug, administeredDose: HSAdministeredDose, patientId: number) => getPackedMedicationForAdministeredDrug(administeredDrug, administeredDose, patientId, this.stores);
  public inactiveScheduleItems = (activeRounds: HSDoseRound[], items: RoundScheduleItem[]): RoundScheduleItem[] => inactiveScheduleItems(activeRounds, items, this.stores);
  public getScheduleFromRound = (round: HSDoseRound, groupPermissions: GroupPermissionsRes, residentDetailUtils: ResidentDetailsUtils, scheduleItems: RoundScheduleItem[]) => getScheduleFromRound(round, this.stores, groupPermissions, residentDetailUtils, scheduleItems);
  public getPatientsFromRound = (round: HSDoseRound) => getPatientsFromRound(round, this.stores.patientStore);
  public getRoundsFromPatient = (patient: HSPatient) => getRoundsFromPatient(this.stores.roundStore, patient);
  public getPackedMedicationsForPatient = (controlledDrugs: boolean, patientId: number, interval: Interval) =>
    getPackedMedicationsForPatient(controlledDrugs, patientId, interval, this.stores);

  public getTimeCriticalMedicationsForPatient = (patientId: number, interval: Interval) =>
    getTimeCriticalMedicationsForPatient(patientId, interval, this.stores);

    // Filters the supplied array of RoundScheduleItems, returning only those that contain a HSPackedMedication that is
    // flagged as timeCritical.
    public getTimeCriticalScheduleItemsForRounds= (
        items: RoundScheduleItem[]
    )=> getTimeCriticalScheduleItemsForRounds(items);

    // Gets an array of objects consisting of HSAdministeredDrugs and linked HSDoseRound's clinicalSystemId if any exist
    // for the supplied HSPackedMedication and patient within the supplied HSDoseRound[] array round window.
    // Generally, should only be one AdministeredDrug for the HSPackedMedication that was administered but will sort
    // multiples in descending order of the administeredAt timestamp.
    public getSortedHistoricalAdministrationsForPatientAndPackedMedication = (
        patientId: number,
        packedMedication: HSPackedMedication,
        rounds: HSDoseRound[]
    ) => getSortedHistoricalAdministrationsForPatientAndPackedMedication(patientId, packedMedication, rounds);

    public getRoundWindow = (date: Date, facilityGroupId: number) =>
    getRoundWindow(date, facilityGroupId, this.stores.facilityGroupConfigurationStore);
  public getMostRecentDrugInRoundByPackedMedication = getMostRecentDrugInRoundByPackedMedication;
  public getPatientsToBeSeenWithinRound = (
    round: HSDoseRound,
    roundSchedule: RoundScheduleItem[],
    requireSyringeDrivers: boolean,
  ) => getPatientsToBeSeenWithinRound(round, roundSchedule, requireSyringeDrivers);
  public getPatientsSeenWithinRound = (
    round: HSDoseRound,
    roundSchedule: RoundScheduleItem[],
    requireSyringeDrivers: boolean,
  ) => getPatientsSeenWithinRound(round, roundSchedule, this.stores, requireSyringeDrivers);
  public getRoundSummaryItems = (
    round: HSDoseRound,
    roundSchedule: RoundScheduleItem[],
    requireSyringeDrivers: boolean,
  ) => getRoundSummaryItems(round, roundSchedule, requireSyringeDrivers);
  public getPrnSummaryItems = (round: HSDoseRound) => getPrnSummaryItems(round, this.stores);
  public getNimSummaryItems = (round: HSDoseRound) => getNimSummaryItems(round, this.stores);
  public getActiveRounds = (date: Date, facilityIds: number[]) => getActiveRounds(date, facilityIds, this.stores);
  public getIdsOfFacilitiesInFacilityGroup = (facilityGroupId?: number) =>
    getIdsOfFacilitiesInFacilityGroup(facilityGroupId, this.stores.facilityStore);
  public getFacilitiesInRound = (round: HSDoseRound) => getFacilitiesInRound(round, this.stores.facilityStore);
  public getPackedMedicationStatus = (packedMedication: HSPackedMedication, patientId: number) =>
    getPackedMedicationStatus(packedMedication, patientId, this.stores.roundStore);
  public createRoundSegmentOp = createRoundSegmentOp;
  public createLeaveRoundOp = createLeaveRoundOp;
  public activeUserRounds = (userSubjectId: string, facilityGroupId: number) =>
    activeUserRounds(userSubjectId, this.stores, facilityGroupId);
  public drugInRoundWithControlledStatus = (packedMedication: HSPackedMedication[], controlled: boolean) =>
    drugInRoundWithControlledStatus(packedMedication, this.stores.drugStore, controlled);
  public getAdministeredDrugsForPackedMedication = (packedMedication: HSPackedMedication, patientId: number) =>
    getAdministeredDrugsForPackedMedication(packedMedication, patientId, this.stores.roundStore);
  public selfAdministerSubRoutine = async (params: ISelfAdministerParams) =>
    await selfAdministerSubRoutine(params, this.stores.drugStore);
}

export interface SyringeDriverRunItem {
    scheduledActivity?: SyringeDriverRunScheduledActivityDto;
    performedActivity?: SyringeDriverRunActivityDto;
    doseTimestamp: string;
}
export interface PatchApplicationItem {
    scheduledActivity?: PatchApplicationScheduledActivityDto;
    performedActivity?: PatchApplicationActivityDto;
    doseTimestamp: string;
}
export interface RoundScheduleItem {
  patient: HSPatient;
  packedMedication: HSPackedMedication;
  scheduledActivityPending?: boolean;
  syringeDriverRunActivity?: SyringeDriverRunItem;
  patchApplicationActivity?: PatchApplicationItem;
}

function isPatchSighting(scheduleItem: RoundScheduleItem) {
    return scheduleItem.patchApplicationActivity?.scheduledActivity?.scheduledActivityType === PatchApplicationScheduledActivityTypeDto.Sight ||
        scheduleItem.patchApplicationActivity?.performedActivity?.activityType === PatchApplicationActivityTypeDto.Sighted;
}
// determine whether a drug is S8, given its HSId
function isControlledDrugId(drugId: number, drugStore: ReadonlyMap<string, HSDrug>): boolean {
  const drug = drugStore.get(drugId.toString());

  // Find the warnings for this drug.
  const allDrugWarnings = drug !== undefined ? DrugUtils.getDrugWarnings(drug) : undefined;

  // Are any of the warnings for "controlled drugs"?
  return allDrugWarnings?.controlledDrug === true;
}
function isInsulinDrugId(drugId: number, drugStore: ReadonlyMap<string, HSDrug>): boolean {
  const drug = drugStore.get(drugId.toString());
  return drug?.isInsulin === true;
}

function getFilterTypeFromString(s?: string): FilterDrugType | undefined {
  if (!s || s === 'All') {
    return undefined;
  }
  return s as FilterDrugType;
}
function getFilteredMedicationsByDrugType(
  drugStore: ReadonlyMap<string, HSDrug>,
  packedMedication: HSPackedMedication[],
  drugType?: FilterDrugType,
): HSPackedMedication[] {
  return (
    packedMedication.filter(
      (pm) =>
        !drugType ||
        (drugType === FilterDrugType.Controlled && isControlledDrugId(pm.drugHsId!, drugStore)) ||
        (drugType === FilterDrugType.NonControlled && !isControlledDrugId(pm.drugHsId!, drugStore)) ||
        (drugType === FilterDrugType.TimeCritical && pm.timeCritical) ||
        (drugType === FilterDrugType.Insulin && isInsulinDrugId(pm.drugHsId!, drugStore)),
    ) ?? []
  );
}

function drugInRoundWithControlledStatus(packedMedication: HSPackedMedication[], drugStore: ReadonlyMap<string, HSDrug>, controlled: boolean) {
    return !!packedMedication.find((m) => !!m.drugHsId && isControlledDrugId(m.drugHsId, drugStore) === controlled);
}


function canAdministerEverything(groupPermissions: GroupPermissionsRes)
{
    return groupPermissions.canAdministerNonPackedMedicationsIncludingInjectableMedication &&
        groupPermissions.canAdministerSlidingScaleMedication &&
        groupPermissions.canAdministerShortCourseMedication &&
        groupPermissions.canAdministerControlledDrugs &&
        groupPermissions.canAdministerDoubleSigningMedication &&
        groupPermissions.canOperateSyringeDriverMedication;

}
// Indicates whether the medication can be administered.
// Note that the secondCheckable flag, will reflect the packedMedsDoNotNeedSecondCheck.
function canAdminister(groupPermissions: GroupPermissionsRes, secondCheckable: boolean, patchSighting: boolean, medicationInformation: MedicationInformation): boolean {

    if (medicationInformation.patch && patchSighting && groupPermissions.canSightPatch) {
        return true;
    }

    if (!medicationInformation.packed && medicationInformation.injectable && !groupPermissions.canAdministerNonPackedMedicationsIncludingInjectableMedication) {
        // Not allowed to administer injectable original container meds.
        return false;
    }
    if (!medicationInformation.packed && !groupPermissions.canAdministerNonPackedMedicationsIncludingInjectableMedication && !groupPermissions.canAdministerNonPackedMedicationsExcludingInjectableMedication) {
        // Not allowed to administer original container meds at all.
        return false;
    }
    if (medicationInformation.warfarin && !groupPermissions.canAdministerSlidingScaleMedication) {
        return false;
    }
    if (medicationInformation.shortCourse && !groupPermissions.canAdministerShortCourseMedication) {
        return false;
    }
    if (medicationInformation.controlled && medicationInformation.packed && !groupPermissions.canAdministerPackedControlledDrugs && !groupPermissions.canAdministerControlledDrugs) {
        return false;
    }
    if (medicationInformation.controlled && !medicationInformation.packed && !groupPermissions.canAdministerControlledDrugs) {
        return false;
    }
    if (secondCheckable && !groupPermissions.canAdministerDoubleSigningMedication) {
        return false;
    }
    if (medicationInformation.syringeDriver && !groupPermissions.canOperateSyringeDriverMedication) {
        return false;
    }

    return true;
}
function isScheduleItemVisible(facilityGroupId: number,
    groupPermissions: GroupPermissionsRes,
    drugStore: ReadonlyMap<string, HSDrug>,
    residentDetailsUtils: ResidentDetailsUtils,
    scheduleItem: RoundScheduleItem,
    selectedDrugTypeControlled?: FilterDrugTypeControlled,
    selectedDrugTypeOther?: FilterDrugTypeOther
) {
    const drug = scheduleItem.packedMedication.drugHsId
        ? drugStore.get(scheduleItem.packedMedication.drugHsId.toString())
        : undefined;
    if (!drug) {
        return false;
    }
    const medicationInformation = DrugUtils.getMedicationInformation(scheduleItem.packedMedication, drug);

    function controlledFilterVisible() {
        if (!selectedDrugTypeControlled) {
            return true;
        }
        if (selectedDrugTypeControlled === FilterDrugTypeControlled.Administrable) {
            const secondCheckable = residentDetailsUtils.isMedicationSecondCheckable(facilityGroupId, groupPermissions.packedMedsDoNotNeedSecondCheck ?? false, scheduleItem.packedMedication);
            if (canAdminister(groupPermissions, secondCheckable, isPatchSighting(scheduleItem), medicationInformation)) {
                return true;
            }
        } else if (selectedDrugTypeControlled === FilterDrugTypeControlled.Controlled) {
            if (medicationInformation.controlled) {
                return true;
            }
        } else if (!medicationInformation.controlled) {
            // Only want uncontrolled and this is controlled.
            return true;
        }
        return false;
    }
    function otherFilterVisible() {
        if (!selectedDrugTypeOther) {
            return true;
        }

        if ((selectedDrugTypeOther & FilterDrugTypeOther.TimeCritical) === FilterDrugTypeOther.TimeCritical) {
            if (medicationInformation.timeCritical) {
                return true;
            }
        }
        if ((selectedDrugTypeOther & FilterDrugTypeOther.Insulin) === FilterDrugTypeOther.Insulin) {
            if (medicationInformation.insulin) {
                return true;
            }
        }
        if ((selectedDrugTypeOther & FilterDrugTypeOther.Patch) === FilterDrugTypeOther.Patch) {
            if (medicationInformation.patch) {
                return true;
            }
        }
        if ((selectedDrugTypeOther & FilterDrugTypeOther.Injection) === FilterDrugTypeOther.Injection) {
            if (medicationInformation.injectable) {
                return true;
            }
        }
        return false;
    }


    // Each of the different filters are 'or-ed', and then the results are and-ed together.
    // e.g. if you select controlled drugs only and time critical or syringe, then you should see
    // only controlled time criticals, or controlled syringes.
    return controlledFilterVisible() && otherFilterVisible();
}
function filterScheduleItems(facilityGroupId: number,
    groupPermissions: GroupPermissionsRes,
    drugStore: ReadonlyMap<string, HSDrug>,
    residentDetailsUtils: ResidentDetailsUtils,
    scheduleItems: RoundScheduleItem[],
    selectedDrugTypeControlled?: FilterDrugTypeControlled,
    selectedDrugTypeOther?: FilterDrugTypeOther
) : RoundScheduleItem[] {

    return scheduleItems.filter((entry) => isScheduleItemVisible(facilityGroupId, groupPermissions, drugStore, residentDetailsUtils,entry, selectedDrugTypeControlled, selectedDrugTypeOther));
}
export function isInInterval(tm: string | undefined | null, interval: Interval): boolean {
    if (!tm) {
        return false;
    }
    const dt = DateUtils.toDate(tm);
    const intervalStart = DateUtils.toDate(interval.start);
    const intervalEnd = DateUtils.toDate(interval.end);
    return dt.valueOf() >= intervalStart.valueOf() && dt.valueOf() < intervalEnd.valueOf();

}
function getSyringeDriverRunScheduleFromParameters(
    interval: Interval,
    facilityIds: number[],
    stores: RoundUtilStores): RoundScheduleItem[] {
    const result: RoundScheduleItem[] = [];
    for (let run of stores.syringeDriverRunStore.values()) {
        if (!facilityIds.includes(run.wardId!)) {
            // Wrong facility group.  Ignore.
            continue;
        }
        let packedMedications: HSPackedMedication[] = [];
        if (run.administeredDrugs) {
            for (let administeredDrug of run.administeredDrugs) {
                // Find the packed medication for this administered drug.
                const roundHierarchy = getRoundHierarchyForAdministeredDrug(stores.roundStore, administeredDrug.administeredDrugClinicalSystemId ?? undefined, administeredDrug.administeredDrugId ?? undefined);
                if (roundHierarchy?.administeredDose) {
                    const packedMed = getPackedMedicationForAdministeredDrug(roundHierarchy.administeredDrug, roundHierarchy?.administeredDose, run.patientId!, stores);
                    if (packedMed) {
                        packedMedications.push(packedMed);
                    }
                }
            }
        }
        if (packedMedications.length > 0) {
            if (run.scheduledActivities) {
                for (let scheduledActivity of run.scheduledActivities) {
                    if ((scheduledActivity.status !== SyringeDriverRunScheduledActivityStatusDto.Inactive) && isInInterval(scheduledActivity.scheduledAt, interval)) {
                        for (let packedMedication of packedMedications) {
                            const scheduledItem: RoundScheduleItem = {
                                syringeDriverRunActivity: {
                                    scheduledActivity: scheduledActivity,
                                    doseTimestamp: scheduledActivity.scheduledAt!
                                },
                                patient: stores.patientStore.get(run.patientId!.toString())!,
                                packedMedication: packedMedication,
                                scheduledActivityPending: scheduledActivity.status === SyringeDriverRunScheduledActivityStatusDto.Pending
                            };
                            if (!!scheduledItem.patient) {
                                result.push(scheduledItem);
                            }
                        }
                    }
                }
            }
        }
    }
    return result;
    
}
function getPatchApplicationScheduleFromParameters(
    interval: Interval,
    facilityIds: number[],
    stores: RoundUtilStores): RoundScheduleItem[] {
    const result: RoundScheduleItem[] = [];
    for (let application of stores.patchApplicationStore.values()) {
        if (!facilityIds.includes(application.wardId!)) {
            // Wrong facility group.  Ignore.
            continue;
        }
        let packedMedication: HSPackedMedication | undefined;
        if (application.lastAppliedPatchDrugAdministration?.administeredDrugClinicalSystemId) {
            const administeredDrug = application.lastAppliedPatchDrugAdministration!;
            // Find the packed medication for this administered drug.
            const roundHierarchy = getRoundHierarchyForAdministeredDrug(stores.roundStore, administeredDrug.administeredDrugClinicalSystemId ?? undefined, administeredDrug.administeredDrugId ?? undefined);
            if (roundHierarchy?.administeredDose) {
                packedMedication = getPackedMedicationForAdministeredDrug(roundHierarchy.administeredDrug, roundHierarchy?.administeredDose, application.patientId!, stores);
            }
        }
        if (!!packedMedication) {
            if (application.scheduledActivities) {
                for (let scheduledActivity of application.scheduledActivities) {
                    if ((scheduledActivity.status === PatchApplicationScheduledActivityStatusDto.Pending || scheduledActivity.status === PatchApplicationScheduledActivityStatusDto.Missed || scheduledActivity.status === PatchApplicationScheduledActivityStatusDto.Performed) && isInInterval(scheduledActivity.scheduledAt, interval)) {
                        const scheduledItem: RoundScheduleItem = {
                            patchApplicationActivity: {
                                scheduledActivity: scheduledActivity,
                                doseTimestamp: scheduledActivity.scheduledAt!
                            },
                            patient: stores.patientStore.get(application.patientId!.toString())!,
                            packedMedication: packedMedication,
                            scheduledActivityPending: scheduledActivity.status === PatchApplicationScheduledActivityStatusDto.Pending
                        };
                        if (!!scheduledItem.patient) {
                            result.push(scheduledItem);
                        }
                    }
                }
            }
            // Add in the unscheduled activities.
            if (!!application.activities) {
                for (let activity of application.activities) {
                    if (activity.status !== PatchApplicationStatusDto.Inactive && isInInterval(activity.clientUpdatedAt, interval) && (activity.scheduledActivityId ?? -1) <= 0) {
                        const scheduledItem: RoundScheduleItem = {
                            patchApplicationActivity: {
                                performedActivity: activity,
                                doseTimestamp: activity.clientUpdatedAt!
                            },
                            patient: stores.patientStore.get(application.patientId!.toString())!,
                            packedMedication: packedMedication,
                            scheduledActivityPending: false
                        };
                        if (!!scheduledItem.patient) {
                            result.push(scheduledItem);
                        }
                    }
                }
            }
        }
    }
    return result;

}

// eslint-disable-next-line max-lines-per-function
function getScheduleFromParameters(
  interval: Interval,
  facilityIds: number[],
  stores: RoundUtilStores,
    // Typically this should be false as we usually only want to get RoundScheduleItems that haven't yet been administered
    includeAdministered: boolean): RoundScheduleItem[] {
  const patientUtils = new PatientUtils({
    packedDayStore: new Map(),
    roundStore: new Map(),
    packedPrnStore: new Map(),
    facilityStore: new Map(),
    drugStore: new Map(),
    testResultsStore: new Map(),
    patientStore: stores.patientStore,
    patchApplicationStore: new Map(), 
    facilityGroupConfigurationStore: new Map(),
    syringeDriverRunStore: new Map()
  });
  const residentDetailsUtils = new ResidentDetailsUtils({
    ...stores,
    secondCheckStore: new Map(),
    nimAvailableDrugStore: new Map(),
    testResultsStore: new Map(),
  });
  // the packed medications are the source of truth for the daily medication schedule.
  // get packed days for given facilities

  const patients = patientUtils
    .getActivePatients()
    .filter((patient) => !patient.suspended) //exclude offsite patients from rounds
    .toArray();

  // These will get filtered out from the final return list, so if the param includeAdministered is passed true then we don't want to filter them out.
  const administeredDosesWithinInterval = includeAdministered
      ? []
      : itiriri(stores.roundStore.values())
    .filter((r) => facilityIds.some((id) => r.administeredDoses?.some((d) => d.facilityId === id)))
    .toArray()
    .flatMap((r) => r.administeredDoses ?? [])
    .filter((d) => !!d.doseTimestamp && DateUtils.isTimeBetween(d.doseTimestamp, interval));

  const administeredMedicationIdsInInterval = administeredDosesWithinInterval.flatMap((d) =>
    d.administeredDrugs
      ? d.administeredDrugs.map((drug) => {
          return { medicationId: drug.medicationId, doseTimeStamp: d.doseTimestamp };
        })
      : [],
  );

  const signingSheetMedicationIds = patients
    .flatMap((patient) => patient.patientProfiles)
    .flatMap((profile) => [...(profile?.allCurrentMedications ?? []), ...(profile?.recentlyDeletedMedications ?? [])])
    .filter((med) => med.showOnSigningSheet)
    .map((med) => med?.hsId);

  const packedDays = itiriri(stores.packedDayStore.values())
    .filter(
      (day) =>
        !!day.packDate &&
        datefns.subDays(datefns.startOfDay(DateUtils.toDate(interval.start)), 14) <=
          DateUtils.toOffsetlessDate(day.packDate),
    )
    .map((day) => ({
      ...day,
      packedMedications: day.packedMedications?.filter((med) => signingSheetMedicationIds.includes(med.medicationId)),
    }))
    .toArray();

  const result: RoundScheduleItem[] = [];
  for (let packedDay of packedDays.filter((m) => m.facilityId !== undefined && facilityIds.includes(m.facilityId))) {
      const patient = stores.patientStore.get(packedDay.patientId!.toString());
      if (!!patient) {
          for(let pm of packedDay?.packedMedications ?? []) {
              const drug = pm.drugHsId
                  ? stores.drugStore.get(pm.drugHsId.toString())
                  : undefined;
              const time = pm?.doseTimestamp;
              if (!!drug && isMedicationActive(pm) && !!time && DateUtils.isTimeBetween(time, interval) &&
                  !(drug && PatchUtils.isPatchRemoval(drug)) &&                  
                  (pm.medicationId !== undefined &&
                      !administeredMedicationIdsInInterval.some(
                          (m) =>
                              m.medicationId === pm.medicationId &&
                              m.doseTimeStamp === pm.doseTimestamp,
                      ))) {
                  result.push( {
                      patient: patient,
                      packedMedication: pm,
                  });
              }

          }
      }
  }
  return result;
}

function scheduleItemMatchesFilter(item: RoundScheduleItem,
    filterType: FilterDrugType,
    stores: RoundUtilStores): boolean {
    const drug = stores.drugStore.get(item.packedMedication?.drugHsId?.toString() ?? "-1");
    if (!drug) {
      return false;
    }
    if (filterType === FilterDrugType.Controlled) {
      return DrugUtils.getDrugWarnings(drug).controlledDrug;
    }
    if (filterType === FilterDrugType.NonControlled) {
      return !DrugUtils.getDrugWarnings(drug).controlledDrug;
    }
    if (filterType === FilterDrugType.TimeCritical) {
      return item.packedMedication?.timeCritical ?? false;
    }
    if (filterType === FilterDrugType.Insulin) {
      return drug.isInsulin ?? false;
    }
    // Should never get here.
    return false;

}

// eslint-disable-next-line sonarjs/cognitive-complexity
function compareScheduleItemWithAdministeredDose(item: RoundScheduleItem, administeredDose: HSAdministeredDose, filter: FilterDrugType | undefined, stores: RoundUtilStores) {
  // Dose the patient match.
  if (item.patient.hsId !== administeredDose.patientId) {
    return false;
  }

  // Dose the time match.
  if (!item.packedMedication.doseTimestamp && !item.packedMedication.doseTimestamp) {
    return true;
  }
  if (!item.packedMedication.doseTimestamp || !item.packedMedication.doseTimestamp) {
    return false;
  }
  if (!administeredDose?.doseTimestamp) {
    return false;
  }
  let tm: Date | undefined;
  if (item.syringeDriverRunActivity?.doseTimestamp) {
      tm = DateUtils.toDate(item.syringeDriverRunActivity?.doseTimestamp)
  }
  if (!tm && item.patchApplicationActivity?.doseTimestamp) {
    tm = DateUtils.toDate(item.patchApplicationActivity?.doseTimestamp)
  }
  if (!tm) {
      tm = DateUtils.toOffsetlessDate(item.packedMedication.doseTimestamp!); 
  }
  
  if (DateUtils.compareDates(DateUtils.toOffsetlessDate(administeredDose.doseTimestamp!), tm) !== 0) {
    return false;
  }
  if (!filter) {
    // No filter, so it matches.
    return true;
  }

  // Filter specified.  Check that it matches.
  return scheduleItemMatchesFilter(item, filter, stores);
}

function inactiveScheduleItems(activeRounds: HSDoseRound[], items: RoundScheduleItem[], stores: RoundUtilStores): RoundScheduleItem[] {
  const scheduleItemsInActiveRound: RoundScheduleItem[] = [];
  for(const round of activeRounds) {
      const filterType = getFilterTypeFromString(round.description ?? "All");
      for(const administeredDose of round.administeredDoses ?? []) {
        scheduleItemsInActiveRound.push(...items.filter(x => compareScheduleItemWithAdministeredDose(x, administeredDose, filterType, stores)));
      }
  }
  // Now remove all of the items that are in active rounds.
  return items.filter(x => (!x.syringeDriverRunActivity || 
      (!!x.syringeDriverRunActivity.scheduledActivity &&
          x.syringeDriverRunActivity.scheduledActivity.status !== SyringeDriverRunScheduledActivityStatusDto.Performed &&
          x.syringeDriverRunActivity.scheduledActivity.status !== SyringeDriverRunScheduledActivityStatusDto.Inactive)) &&
      (!x.patchApplicationActivity || (
          !!x.patchApplicationActivity.scheduledActivity && 
          x.patchApplicationActivity.scheduledActivity.status !== PatchApplicationScheduledActivityStatusDto.Performed &&
          x.patchApplicationActivity.scheduledActivity.status !== PatchApplicationScheduledActivityStatusDto.Inactive)
      ) &&
      !scheduleItemsInActiveRound.includes(x));
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function getScheduleFromRound(round: HSDoseRound, stores: RoundUtilStores, groupPermissions: GroupPermissionsRes, residentDetailUtils: ResidentDetailsUtils, scheduleItems: RoundScheduleItem[]): RoundScheduleItem[] {

  //Get the Filters and convert it to their types
  const roundFilterDrugTypeControlled = getRoundFilterDrugTypeControlled(round);
  const roundFilterDrugTypeOther = getRoundFilterDrugTypeOther(round);

  const facilityGroupId = getFacilityGroupIdByFacilityId(round.facilityId!, stores);
  // map the already administered drugs to a schedule.
  let alreadyAdministered = round.administeredDoses
    ? round.administeredDoses.flatMap((dose) => getScheduleFromDose(dose, stores))
    : [];
 // Add in all of the syringe driver run activities that have been performed
const doseTimes = round.administeredDoses ? round.administeredDoses.map(x => DateUtils.toDate(x.doseTimestamp!)) : [];
if (doseTimes.length > 0) {
    // Find any syringe driver run activities that have been performed, and that whose dose timestamp matches one of the timestamps in the round.
    const sdrActivities = scheduleItems.filter(x => !!x.syringeDriverRunActivity && x.syringeDriverRunActivity.scheduledActivity?.status === SyringeDriverRunScheduledActivityStatusDto.Performed && doseTimes.includes(DateUtils.toDate(x.syringeDriverRunActivity.doseTimestamp)));
    const patchActivities = scheduleItems.filter(x => !!x.patchApplicationActivity && x.patchApplicationActivity.scheduledActivity?.status === PatchApplicationScheduledActivityStatusDto.Performed && doseTimes.includes(DateUtils.toDate(x.patchApplicationActivity.doseTimestamp)));
    alreadyAdministered = [...alreadyAdministered, ...sdrActivities, ...patchActivities];
}

  const toBeAdministered = filterScheduleItems(facilityGroupId, groupPermissions, stores.drugStore, residentDetailUtils, scheduleItems, roundFilterDrugTypeControlled, roundFilterDrugTypeOther)
        .filter((x) => {
            // The nurse can now pick individual dose times to administer.
            // This filter matches the available round dose times with those selected by the nurse.
            if (!round.administeredDoses) {
                return true;
            }

            // Safely choose the timestamp to compare against
            const rawDoseTime =
                x.syringeDriverRunActivity?.doseTimestamp ??
                x.patchApplicationActivity?.doseTimestamp ??
                x.packedMedication?.doseTimestamp;

            const doseTime =
                rawDoseTime && typeof rawDoseTime === 'string'
                    ? DateUtils.toOffsetlessDate(rawDoseTime)
                    : (rawDoseTime as Date | undefined);

            return round.administeredDoses.some((y) => {
                if (y.patientId !== x.patient.hsId) {
                    return false;
                }

                const ts = y.doseTimestamp;
                if (!ts) {
                    // PRNs don't have a timestamp.
                    return false;
                }
                return DateUtils.compareDates(DateUtils.toOffsetlessDate(ts), doseTime) === 0;
            });
        });

  return alreadyAdministered.concat(toBeAdministered);
}

export function getPackedMedForAdministeredDrug(administeredDrug: HSAdministeredDrug, administeredDose: HSAdministeredDose,  patientId: number, ppdStore: ReadonlyMap<string, HSPackedPatientDay>) {
    return itiriri(ppdStore.values())
        .filter((p) => {
            return !!administeredDose.doseTimestamp && p.patientId === patientId && isSameDay(DateUtils.toOffsetlessDate(p.packDate!), DateUtils.toDate(administeredDose.doseTimestamp!))
        })
        .flat((p) => p.packedMedications ?? [])
        .filter((p) => administeredDrug.medicationId == p.medicationId! && p.doseTimestamp === administeredDose.doseTimestamp)
        .first();
    
} 
function getPackedMedicationForAdministeredDrug(administeredDrug: HSAdministeredDrug, administeredDose: HSAdministeredDose,  patientId: number, stores: RoundUtilStores) {
    return getPackedMedForAdministeredDrug(administeredDrug, administeredDose, patientId, stores.packedDayStore);
}
function getScheduleFromDose(dose: HSAdministeredDose, stores: RoundUtilStores): RoundScheduleItem[] {
  const patientUtils = new PatientUtils({
    packedDayStore: stores.packedDayStore,
    roundStore: stores.roundStore,
    packedPrnStore: stores.packedPrnStore,
    facilityStore: stores.facilityStore,
    drugStore: stores.drugStore,
    testResultsStore: new Map(),
    patientStore: stores.patientStore,
    patchApplicationStore: stores.patchApplicationStore, 
    facilityGroupConfigurationStore: stores.facilityGroupConfigurationStore,
    syringeDriverRunStore: stores.syringeDriverRunStore,
  });

  const patient = patientUtils.getActivePatients().find((p) => p.hsId === dose.patientId);
  if (!patient || !dose.administeredDrugs) {
    return [];
  }
  const medicationIds = dose.administeredDrugs.flatMap((d) => (d.medicationId ? [d.medicationId] : []));
  const packedMedications = itiriri(stores.packedDayStore.values())
    .filter((p) => p.patientId === dose.patientId)
    .flat((p) => p.packedMedications ?? [])
    .filter((p) => medicationIds.includes(p.medicationId!) && dose.doseTimestamp === p.doseTimestamp)
    .toArray();

  return packedMedications.map((m) => {
    return {
      patient: patient,
      packedMedication: m,
    };
  });
}

function getPatientsFromRound(round: HSDoseRound, patientStore: ReadonlyMap<string, HSPatient>): HSPatient[] {
  const patientUtils = new PatientUtils({
    packedDayStore: new Map(),
    roundStore: new Map(),
    packedPrnStore: new Map(),
    facilityStore: new Map(),
    drugStore: new Map(),
    testResultsStore: new Map(),
    patientStore: patientStore,
    patchApplicationStore: new Map(), 
    facilityGroupConfigurationStore: new Map(),
    syringeDriverRunStore: new Map()
  });
  return patientUtils
    .getActivePatients()
    .filter(
      (p) =>
        round.administeredDoses !== undefined &&
        round.administeredDoses !== null &&
        round.administeredDoses.map((d) => d.patientId).includes(p.hsId),
    )
    .toArray();
}

function getRoundsFromPatient(roundStore: ReadonlyMap<string, HSDoseRound>, patient: HSPatient): HSDoseRound[] {
  return itiriri(roundStore.values())
    .filter(
      (r) =>
        !!r.administeredDoses &&
        r.administeredDoses.length > 0 &&
        r.administeredDoses.map((d) => d.patientId).includes(patient.hsId),
    )
    .sort((a, b) => DateUtils.toDate(a.createdAt!).getTime() - DateUtils.toDate(b.createdAt!).getTime())
    .toArray();
}

function getPackedMedicationAdminstrationReason(
  packedMedication: HSPackedMedication,
  round: HSDoseRound,
): ReasonCode | undefined {
  if (!isMedicationActive(packedMedication)) {
    return ReasonCode.Ceased;
  }

  const drugsWithTimes = round.administeredDoses!.flatMap((dose) =>
    dose.administeredDrugs && dose.doseTimestamp
      ? dose!.administeredDrugs!.map((d) => {
          return { drug: d, time: dose.doseTimestamp! };
        })
      : [],
  );

  return drugsWithTimes.find(
    (drugTime) =>
      drugTime.drug.medicationId === packedMedication.medicationId! && drugTime.time === packedMedication.doseTimestamp,
  )?.drug.reasonCode;
}

export interface PackedSummaryItem {
  patient: HSPatient;
  packedMedication: HSPackedMedication;
  reason?: ReasonCode;
}

function evaluateScheduleActivityReasonCode(scheduleItem: RoundScheduleItem, requireSyringeDrivers: boolean) {
  //if activity is not pending assume it is dosed
  if ((scheduleItem.syringeDriverRunActivity || scheduleItem.patchApplicationActivity) && !scheduleItem.scheduledActivityPending) {
    return ReasonCode.Dosed;
  }
  return undefined;
}

function getRoundSummaryItems(
  round: HSDoseRound,
  roundSchedule: RoundScheduleItem[],
  requireSyringeDrivers: boolean,
): PackedSummaryItem[] {
  return roundSchedule.map((scheduleItem) => {
    return {
      ...scheduleItem,
      reason: (scheduleItem.syringeDriverRunActivity || scheduleItem.patchApplicationActivity)
        ? evaluateScheduleActivityReasonCode(scheduleItem, requireSyringeDrivers)
        : getPackedMedicationAdminstrationReason(scheduleItem.packedMedication, round),
    };
  });
}

export interface NimSummaryItem {
  patient: HSPatient;
  drugId: number;
  reason?: AdHocReasonCode;
}

function getNimSummaryItems(round: HSDoseRound, stores: RoundUtilStores): NimSummaryItem[] {
  const patientUtils = new PatientUtils({
    packedDayStore: new Map(),
    roundStore: new Map(),
    packedPrnStore: new Map(),
    facilityStore: new Map(),
    drugStore: new Map(),
    testResultsStore: new Map(),
    patientStore: stores.patientStore,
    patchApplicationStore: new Map(),
    facilityGroupConfigurationStore: new Map(),
    syringeDriverRunStore: new Map(),
  });

  if (!round.administeredDoses) {
    return [];
  }
  const nimsAdministeredPerPatient = round.administeredDoses.flatMap((dose) => {
    return {
      patientId: dose.patientId!,
      adHocDrugs: dose.administeredAdHocDrugs,
    };
  });
  return nimsAdministeredPerPatient.flatMap((patientAdHocDrugs) => {
    const patient = patientUtils.getActivePatients().find((p) => p.hsId === patientAdHocDrugs.patientId);
    if (patient && patientAdHocDrugs.adHocDrugs) {
      return patientAdHocDrugs.adHocDrugs?.map((d) => {
        return { patient: patient, drugId: d.drugId!, reason: d.reasonCode };
      });
    } else {
      return [];
    }
  });
}
export interface PrnSummaryItem {
  patient: HSPatient;
  packedPrnMedication: HSPackedPrnMedication;
  reason?: ReasonCode;
}

function getPrnSummaryItems(round: HSDoseRound, stores: RoundUtilStores): PrnSummaryItem[] {
  const patientUtils = new PatientUtils({
    packedDayStore: new Map(),
    roundStore: new Map(),
    packedPrnStore: new Map(),
    facilityStore: new Map(),
    drugStore: new Map(),
    testResultsStore: new Map(),
    patientStore: stores.patientStore,
    patchApplicationStore: new Map(),
    facilityGroupConfigurationStore: new Map(),
    syringeDriverRunStore: new Map(),
  });

  if (!round.administeredDoses) {
    return [];
  }
  const prnsAdministeredPerPatient = round.administeredDoses.flatMap((dose) => {
    return {
      patientId: dose.patientId!,
      prnDrugs: dose.administeredDrugs?.filter((drug) => getPrnMedication(drug.medicationId!, stores) !== undefined),
    };
  });

  return prnsAdministeredPerPatient.flatMap((patientPrns) => {
    const patient = patientUtils.getActivePatients().find((p) => p.hsId === patientPrns.patientId);
    if (patient && patientPrns.prnDrugs) {
      return patientPrns.prnDrugs?.map((d) => {
        return {
          patient: patient,
          packedPrnMedication: getPrnMedication(d.medicationId!, stores)!,
          reason: d.reasonCode,
        };
      });
    } else {
      return [];
    }
  });
}

function getPrnMedication(medicationId: number, stores: RoundUtilStores): HSPackedPrnMedication | undefined {
  return itiriri(stores.packedPrnStore.values())
    .toArray()
    .flatMap((patientPrns) => patientPrns.packedPrnMedications ?? [])
    .find((prn) => prn.medicationId === medicationId);
}

function getPatientsToBeSeenWithinRound(
  round: HSDoseRound,
  roundSchedule: RoundScheduleItem[],
  requireSyringeDrivers: boolean,
): HSPatient[] {
  const summary = getRoundSummaryItems(round, roundSchedule, requireSyringeDrivers);
  return [
    ...new Set(
      summary.filter((summaryItem) => summaryItem.reason === undefined).map((summaryItem) => summaryItem.patient),
    ),
  ];
}

function getPatientsSeenWithinRound(
  round: HSDoseRound,
  roundScheduleItem: RoundScheduleItem[],
  stores: RoundUtilStores,
  requireSyringeDrivers: boolean,
): HSPatient[] {
  const allPatientsInRound = getPatientsFromRound(round, stores.patientStore);
  const patientsToBeSeenInRound = getPatientsToBeSeenWithinRound(round, roundScheduleItem, requireSyringeDrivers);
  return allPatientsInRound.filter((patient) => !patientsToBeSeenInRound.find((p) => p.hsId === patient.hsId));
}

function getPackedMedicationsForPatient(
  controlledDrugs: boolean,
  patientId: number,
  interval: Interval,
  stores: RoundUtilStores,
): HSPackedMedication[] {
  // the packed medications are the source of truth for the daily medication schedule.
  // get packed days for given facilities
  const patient = stores.patientStore.get(patientId.toString());
  return (
    itiriri(stores.packedDayStore.values())
      .filter((m) => m.patientId === patientId)
      .toArray()
      // map to defined packedMedication list and flatten result
      .flatMap((p) => p.packedMedications ?? [])
      .filter((p) =>
        patient?.patientProfiles?.some(
          (profile) =>
            [...(profile.allCurrentMedications ?? []), ...(profile.recentlyDeletedMedications ?? [])].find(
              (med) => med.hsId === p.medicationId,
            )?.showOnSigningSheet,
        ),
      )
      // filter out deleted items
      .filter(isMedicationActive)
      // medication within time
      .filter((m) => !!m.doseTimestamp && DateUtils.isTimeBetween(m.doseTimestamp, interval))
      // give all drugs if `controlledDrugs`, otherwise return only controlled drugs
      .filter((m) => !controlledDrugs || (m.drugHsId !== undefined && isControlledDrugId(m.drugHsId, stores.drugStore)))
  );
}

function getTimeCriticalMedicationsForPatient(
  patientId: number,
  interval: Interval,
  stores: RoundUtilStores,
): HSPackedMedication[] {
  return getPackedMedicationsForPatient(false, patientId, interval, stores).filter((m) => m.timeCritical);
}

// Filters the supplied array of RoundScheduleItems, returning only those that contain a HSPackedMedication that is
// flagged as timeCritical.
function getTimeCriticalScheduleItemsForRounds(items: RoundScheduleItem[]): RoundScheduleItem[] {
    return items.filter((item) => item.packedMedication.timeCritical);
}

// Gets an array of objects consisting of HSAdministeredDrugs and linked HSDoseRound's clinicalSystemId if any exist
// for the supplied HSPackedMedication and patient within the supplied HSDoseRound[] array round window.
// Generally, should only be one AdministeredDrug for the HSPackedMedication that was administered but will sort
// multiples in descending order of the administeredAt timestamp.
function getSortedHistoricalAdministrationsForPatientAndPackedMedication(
    patientId: number,
    packedMedication: HSPackedMedication,
    rounds: HSDoseRound[]
) {
    const adminDoses = rounds
        .flatMap((r) =>
            (r.administeredDoses ?? [])
                .map((ad) =>
                    ({ ...ad, doseRoundClinicalSystemId: r.clinicalSystemId! })),
        ).filter((p) =>
            p.patientId === patientId
            && p.doseTimestamp !== undefined
            && p.doseTimestamp !== null
            && p.doseTimestamp === packedMedication.doseTimestamp);

    if (adminDoses.length === 0) {
        return [];
    }

    return  adminDoses
        .flatMap
        (
            (r) =>
                (r.administeredDrugs ?? []).map((ad) =>
                    ({
                        doseRoundClinicalSystemId: r.doseRoundClinicalSystemId,
                        administeredDrug: ad
                    })
                ),
        )
        .filter((d) => d.administeredDrug.medicationId === packedMedication.medicationId)
        .sort((a, b) =>
            DateUtils.compareDateStringsDescending(a.administeredDrug.administeredAt, b.administeredDrug.administeredAt),
        );
}

// TODO: This should be configurable by the facility.
function getRoundWindow(
  date: Date,
  facilityGroupId: number,
  facilityGroupConfigurationStore: ReadonlyMap<string, FacilityGroupConfigurationDto>,
): Interval {
  const facilityGroupUtils = new FacilityGroupUtils({
    facilityGroupConfigurationStore: facilityGroupConfigurationStore,
  });
  const intervalMinutes = facilityGroupUtils.getRoundIntervalsInMinutes(facilityGroupId);
  const startDate = datefns.subMinutes(date, intervalMinutes);
  const endDate = datefns.addMinutes(date, intervalMinutes);

  const start = DateUtils.fromDate(startDate);
  const end = DateUtils.fromDate(endDate);

  return {
    start,
    end,
  };
}

function getRoundFilterDrugTypeControlled(round: HSDoseRound) {
  if (round.description?.includes('Non-Controlled')) {
    return FilterDrugTypeControlled.NonControlled
  } else if (round.description?.includes('Controlled'))
  {
    return FilterDrugTypeControlled.Controlled;
  } else if (round.description?.includes('Administrable')) {
      return FilterDrugTypeControlled.Administrable;
  }
  return undefined;
}

function getRoundFilterDrugTypeOther(round: HSDoseRound) {
  let roundFilterDrugTypeOther = 0;
  //console.log('round.description: ' + round.description);

  //console.log('round.description?.includes(\'TimeCritical\'):' + round.description?.includes('TimeCritical'))

  //console.log('round.description?.includes(\'Insulin\'):' + round.description?.includes('Insulin'));

  if (round.description?.includes('TimeCritical')) {
    roundFilterDrugTypeOther = (roundFilterDrugTypeOther | FilterDrugTypeOther.TimeCritical);
  }
  if (round.description?.includes('Insulin'))
  {
    roundFilterDrugTypeOther = (roundFilterDrugTypeOther | FilterDrugTypeOther.Insulin);
  }
  if (round.description?.includes('Patch')) {
      roundFilterDrugTypeOther = (roundFilterDrugTypeOther | FilterDrugTypeOther.Patch);
  }
  if (round.description?.includes('Injection')) {
      roundFilterDrugTypeOther = (roundFilterDrugTypeOther | FilterDrugTypeOther.Injection);
  }

  //console.log('roundFilterDrugTypeOther:' + roundFilterDrugTypeOther);
  return roundFilterDrugTypeOther === 0 ? undefined : roundFilterDrugTypeOther;
}

function getMostRecentDrugInRoundByPackedMedication(
  packedMedication: HSPackedMedication,
  patientId: number,
  round?: HSDoseRound,
): HSAdministeredDrug | undefined {
  const dose = round?.administeredDoses?.find(
    (dose) => dose.doseTimestamp === packedMedication.doseTimestamp && dose.patientId === patientId,
  );

  return dose?.administeredDrugs
    ?.sort((a, b) => DateUtils.compareDateStringsDescending(a.administeredAt, b.administeredAt))
    .find((drug) => drug.medicationId === packedMedication.medicationId);
}

function getActiveRounds(date: Date, facilityIds: number[], roundUtilStore: RoundUtilStores): HSDoseRound[] {
  const windowStart = addHours(new Date(), -2);
  return itiriri(roundUtilStore.roundStore.values())
    .filter(
      (r) =>
        r.status !== undefined &&
        !!r.createdAt &&
        (r.status === DoseRoundStatus.InProgress ||
          r.status === DoseRoundStatus.Pending ||
          r.status === DoseRoundStatus.Paused) &&
        facilityIds.some(
          (id) =>
            r.administeredDoses !== undefined &&
            r.administeredDoses !== null &&
            r.administeredDoses.flatMap((d) => d.facilityId).includes(id),
        ) &&
          // Returns +ve if window start is before the round start.
        DateUtils.compareDates(windowStart, DateUtils.toDate(r.createdAt)) > 0,
    )
    .toArray();
}

function getIdsOfFacilitiesInFacilityGroup(
  facilityGroupId: number | undefined,
  store: ReadonlyMap<string, WardDto>,
): number[] {
  return itiriri(store.values())
    .filter((f) => facilityGroupId !== undefined && f.hsId !== undefined && f.facilityGroupId === facilityGroupId)
    .map((f) => f.hsId!)
    .toArray();
}

function getFacilitiesInRound(round: HSDoseRound, store: ReadonlyMap<string, WardDto>): WardDto[] {
  const facilityIds = round.administeredDoses?.map((d) => d.facilityId);
  return itiriri(store.values())
    .filter((f) => f.hsId !== undefined && facilityIds !== undefined && facilityIds.includes(f.hsId))
    .toArray();
}

function getPackedMedicationStatus(
  packedMedication: HSPackedMedication,
  patientId: number,
  store: ReadonlyMap<string, HSDoseRound>,
): ReasonCode | undefined {
  if (packedMedication.medicationStatus === MedicationStatus.Deleted || packedMedication.interimDeleted) {
    return ReasonCode.Ceased;
  }

  let administeredDrugs: HSAdministeredDrug[] = [];

  // Find all the rounds that match.
  itiriri(store.values()).forEach((round) => {
    // Find the doses that match the required time.
    round?.administeredDoses?.filter((dose) => dose.doseTimestamp === packedMedication.doseTimestamp).forEach((dose) => {
      // Find the meds that match the required med.
      dose.administeredDrugs?.filter((dr) => dr.medicationId === packedMedication.medicationId)?.forEach((drug) => {
        if (drug.medicationId === packedMedication.medicationId) {
          administeredDrugs.push(drug);
        }
      })
    });
  });

  if (administeredDrugs.length == 0) {
    return undefined;
  }
  // Return the reason code of the most recent one.
  const mostRecentDose = administeredDrugs?.sort((a, b) => DateUtils.compareDateStringsDescending(a.administeredAt, b.administeredAt));
  return mostRecentDose[0].reasonCode;
}

// TODO: remove or user everywhere
function createRoundSegmentOp(
  facilityGroupId: number,
  doseRoundClinicalSystemId: string,
  userSubjectId: string,
): CreateRoundSegmentOp {
  const joinTime = DateUtils.fromDate(new Date());
  return {
    type: 'round-segment-create',
    doseRoundClinicalSystemId: doseRoundClinicalSystemId,
    request: {
      doseRoundId: -1,
      doseRoundStatus: DoseRoundStatus.InProgress,
      doseRoundSegment: {
        createdAt: joinTime,
        startedAt: joinTime,
        lastUpdatedAt: joinTime,
        startCommentText: 'Joined',
        active: true,
        startedBySubjectId: userSubjectId,
        //startedById: <resolved from mapping table on MRS>
        // endCommentText:
      },
      facilityGroupId: facilityGroupId,
    },
  };
}

function createLeaveRoundOp(
  doseRoundClinicalSystemId: string,
  facilityGroupId: number,
  userSubjectId: string,
  round: HSDoseRound,
  finishRoundReason?: string
): UpdateRoundSegmentOp | undefined {
  const endTime = DateUtils.fromDate(new Date());

  const roundSegment = round.doseRoundSegments?.find(
    (s) => s.startedBySubjectId === userSubjectId && !s.endedBySubjectId,
  );

  if (!roundSegment?.clinicalSystemId) {
    return undefined;
  }


  return {
    type: 'round-segment-update',
    doseRoundClinicalSystemId: doseRoundClinicalSystemId,
    doseRoundSegmentClinicalSystemId: roundSegment.clinicalSystemId,
    request: {
      roundSegment: {
        ...roundSegment,
        endedBySubjectId: userSubjectId,
        endedAt: endTime,
        endCommentText: finishRoundReason? finishRoundReason : 'Left',
        lastUpdatedAt: endTime,
        active: false,
      },
      // Note: round.status is always defined, it is not optional in healthstream, and it is required for this request type.
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      roundStatus: round?.status!,
      facilityGroupId: facilityGroupId,
    },
  };
}

function activeUserRounds(userSubjectId: string, roundUtilStore: RoundUtilStores, facilityGroupId: number) {
  const windowStart = addHours(new Date(), -2);
  return itiriri(roundUtilStore.roundStore.values())
    .filter(
      (r) =>
        r.createdAt !== undefined &&
        (r.status === DoseRoundStatus.InProgress ||
          r.status === DoseRoundStatus.Pending ||
          r.status === DoseRoundStatus.Paused) &&
        r.doseRoundSegments !== undefined &&
        r.doseRoundSegments !== null &&
        r.doseRoundSegments.some(
          (s) =>
            (s.endedBySubjectId === undefined || s.endedBySubjectId === null) && s.startedBySubjectId === userSubjectId,
        ) &&
          // Returns +ve if window start is before the round start.
          DateUtils.compareDates(windowStart, DateUtils.toDate(r.createdAt)) > 0,
    )
    .toArray();
}

function getAdministeredDrugsForPackedMedication(
  packedMedication: HSPackedMedication,
  patientId: number,
  store: ReadonlyMap<string, HSDoseRound>,
) {
  const doses = itiriri(store.values())
    .flat((round) => round.administeredDoses ?? [])
    .filter((dose) => dose.doseTimestamp === packedMedication.doseTimestamp && dose.patientId === patientId);

  return doses
    .flat((dose) => dose.administeredDrugs ?? [])
    .filter((drug) => drug.medicationId === packedMedication.medicationId)
    .toArray();
}

function getFacilityGroupIdByFacilityId(facilityId: number, stores: RoundUtilStores) {
  const facility = stores.facilityStore.get(facilityId.toString());
  // It should be safe enough to assume that the facility group ID always exists
  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
  return facility?.facilityGroupId!;
}

interface ISelfAdministerParams {
  roundSchedule: RoundScheduleItem[];
  drugList: HSDrug[];
  user: User;
  residentDetailsUtils: ResidentDetailsUtils;
  facilityGroupId: string;
  syncRounds: SyncRounds;
  syncTestResults: SyncTestResults;
  round: HSDoseRound;
}

async function selfAdministerSubRoutine(params: ISelfAdministerParams, drugStore: ReadonlyMap<string, HSDrug>) {
  const { roundSchedule, drugList, user, residentDetailsUtils, facilityGroupId, syncRounds, syncTestResults, round } =
    params;

  const groupedRoundScheduleItems = Object.values(groupBy(roundSchedule, (scheduleItem) => scheduleItem.patient.hsId));
  //run a loop for all the medications of the FSA patient
  for (const scheduleItem of groupedRoundScheduleItems) {
    const packedMedications = scheduleItem.map((item) => item.packedMedication);

    for (const packedMedication of packedMedications) {
      const drug = drugStore.get(packedMedication.drugHsId!.toString());
      if (scheduleItem[0].patient && isMedicationSelfAdministerable(packedMedication, scheduleItem[0].patient, drug!)) {
        await selfAdministerMedication(
          packedMedication,
          drugList,
          residentDetailsUtils,
          syncRounds,
          syncTestResults,
          round,
          user,
          scheduleItem[0].patient,
          facilityGroupId,
        );
      }
    }
  }
}

/**
 * this function is used to check if a medication should be self administered by the application based on certain parameters
 * not to be confused by the function isMedicationSelfAdministered from PackedPatientDayUtils
 * which checks the selfAdministered property of an HSMedication object
 */
function isMedicationSelfAdministerable(packedMedication: HSPackedMedication, patient: HSPatient, drug: HSDrug) {
  //check if packed medication has a corresponding HSMedication object to track changes
  const hsMedicationObject = patient.patientProfiles
    ?.flatMap((profile) => profile.allCurrentMedications)
    .find((medication) => medication?.hsId === packedMedication.medicationId);

  const medicationStatus = DrugUtils.getMedicationStatus(hsMedicationObject ?? packedMedication);
  const isPatch = PatchUtils.isPatch(drug);
  if (medicationStatus.isNew || medicationStatus.isChanged || packedMedication.route?.code === 'SID' || isPatch) {
    return false;
  }
  return true;
}

//basically to avoid cognitive complexity
async function selfAdministerMedication(
  medication: HSPackedMedication,
  drugList: HSDrug[],
  residentDetailsUtils: ResidentDetailsUtils,
  syncRounds: SyncRounds,
  syncTestResults: SyncTestResults,
  round: HSDoseRound,
  user: User,
  patient: HSPatient,
  facilityGroupId: string,
) {
  const drug = drugList.find((drug) => drug.hsId === medication.drugHsId);
  //create an administeredDrug object from the medication
  const administeredDrug = residentDetailsUtils.generateAdministeredDrug(
    medication,
    drug!,
    ReasonCode.SelfAdministered,
    [],
    user,
  );
  //enqueue the administered drug
  await DrugAdministrationUtils.administerDrug(
    '',
    [administeredDrug],
    patient,
    parseInt(facilityGroupId),
    syncRounds,
    // FIX: missing a params here. Need to be passed from the parent
    syncTestResults,
    round,
    medication && 'doseTimestamp' in medication ? medication.doseTimestamp ?? '' : '',
  );
}